<template>
  <div>
    <app-modal v-model="isDialogOpened"
               size="lg"
               data-locator="group-import-dialog"
               @cancel="closeDialog"
    >
      <template #title>
        {{ $t('project.members.dialogs.GroupImportDialog.title') }}
      </template>


      <template #body>
        <GroupImportForm
          v-if="dialogContent === GROUP_IMPORT_DIALOG_CONTENT.GROUP_IMPORT"
          ref="groupImportForm"
          v-model:groupImportFormModel="groupImportFormModel"
          v-model:errorCode="massInputError"
          :errorDetails="massInputErrorArray"
          @update:okDisabled="importOkDisabled = $event"
        />
        <MemberSubscription
          v-if="dialogContent ===  GROUP_IMPORT_DIALOG_CONTENT.MEMBER_SUBSCRIPTION"
          ref="memberSubscription"
          :multi="true"
          :errorData="memberSubscriptionErrorData"
          @update:okDisabled="subscriptionOkDisabled = $event"
          @onSubscriptionDecline="cancelUser"
          @onSubscriptionValidate="doImport"
        />

        <template v-if="dialogContent === GROUP_IMPORT_DIALOG_CONTENT.IRRECOVERABLE_ERROR">
          <div>
            {{ $t('project.members.dialogs.GroupImportDialog.error.ERR_FORBIDDEN_INTRO', { subName }) }}
          </div>
          <div v-if="errorCode === 'ERR_FORBIDDEN_FOR_NON_SUB_USER'" class="mt-2">
            {{ $t('project.members.dialogs.GroupImportDialog.error.ERR_FORBIDDEN_FOR_NON_SUB_USER', { subName }) }}
          </div>
          <div v-if="errorCode === 'ERR_FORBIDDEN_FOR_NON_SUB_ADMIN'" class="mt-2">
            {{ $t('project.members.dialogs.GroupImportDialog.error.ERR_FORBIDDEN_FOR_NON_SUB_ADMIN', { subName }) }}
          </div>
          <div v-if="errorCode === 'ERR_FORBIDDEN_FOR_NON_SUB_ADMIN'" class="mt-2">
            <div v-for="admin in subAdminContacts" :key="admin.email">
              - {{ admin.fullName}} ({{ admin.email }})
            </div>
          </div>
        </template>
      </template>

      <template #footer>
        <v-spacer />

        <!-- The subscription step used to be left through a close icon that actually meant
             "go back", indistinguishable from closing. -->
        <app-button v-if="dialogContent === GROUP_IMPORT_DIALOG_CONTENT.MEMBER_SUBSCRIPTION"
                    type="outlined"
                    variant="neutral"
                    data-locator="back-button"
                    @click="dialogContent = GROUP_IMPORT_DIALOG_CONTENT.GROUP_IMPORT"
        >
          {{ $t('common.back') }}
        </app-button>

        <template v-if="dialogContent === GROUP_IMPORT_DIALOG_CONTENT.GROUP_IMPORT">
          <app-button type="outlined" variant="neutral" data-locator="cancel-button" @click="closeDialog">
            {{ $t('common.cancel') }}
          </app-button>
          <app-button :disabled="importOkDisabled"
                      :loading="postMassLoading"
                      data-locator="confirm-button"
                      @click="doImport()"
          >
            {{ $t('project.members.dialogs.GroupImportDialog.import') }}
          </app-button>
        </template>

        <app-button v-else-if="dialogContent === GROUP_IMPORT_DIALOG_CONTENT.MEMBER_SUBSCRIPTION"
                    :disabled="subscriptionOkDisabled"
                    :loading="postMassLoading"
                    data-locator="confirm-button"
                    @click="$refs.memberSubscription.validate()"
        >
          {{ $t('common.ok') }}
        </app-button>

        <app-button v-else-if="dialogContent === GROUP_IMPORT_DIALOG_CONTENT.IRRECOVERABLE_ERROR"
                    type="outlined"
                    variant="neutral"
                    data-locator="cancel-button"
                    @click="closeDialog"
        >
          {{ $t('common.cancel') }}
        </app-button>
      </template>
    </app-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import { IMPORT_SOURCES, track } from '@/common/pendo/agent'
import { PRODUCT_ROOM_TYPES } from '@/common/utils/roomTypes'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'

import GroupImportForm from '../GroupImportForm.vue'
import MemberSubscription from '../MemberSubscription.vue'

 const GROUP_IMPORT_DIALOG_CONTENT = {
  GROUP_IMPORT: 'group-import',
  MEMBER_SUBSCRIPTION: 'member-subscription',
  IRRECOVERABLE_ERROR: 'irrecoverable-error',
}

const getEmptyGroupImportFormModel = (isDataRoom) => ({
  selectedFile: null,
  customMessage: '',
  customMessageIsActive: false,
  sendInvitationNow: true,
  hidePm: false,
  idVerification: !isDataRoom,
})

export default {
  name: 'GroupImportDialog',
  components: {
    MemberSubscription,
    GroupImportForm,
  },
  mixins: [dialogMixin],
  props: {
    mnemo: {
      type: String,
      required: false,
    },
  },
  data () {
    return {
      GROUP_IMPORT_DIALOG_CONTENT,
      dialogContent: GROUP_IMPORT_DIALOG_CONTENT.GROUP_IMPORT,
      errorCode: null,
      groupImportFormModel: getEmptyGroupImportFormModel(this.$store.getters['room/isDataRoom']),
      massInputError: null,
      massInputErrorArray: [],
      importOkDisabled: true,
      memberSubscriptionErrorData: null,
      subscriptionOkDisabled: true,
      subAdminContacts: null,
      subName: null,
      visible: true,
    }
  },
  computed: {
    ...mapState('groups', ['postMassLoading']),
    ...mapGetters('room', ['isDataRoom']),
  },
  methods: {
    ...mapActions('groups', ['POST_MASS_IMPORT', 'GET_GROUPS', 'POST_USER_MISSED_ACCESS']),
    async cancelUser () {
      await this.POST_USER_MISSED_ACCESS({
        mnemo: this.mnemo,
        isImport: true,
        data: {},
      })
      this.closeDialog()
    },
    doImport (params) {
      const formData = new FormData()
      formData.append('file', this.groupImportFormModel.selectedFile, this.groupImportFormModel.selectedFile.name)
      if (this.groupImportFormModel.customMessageIsActive) {
        formData.append('customMessage', this.groupImportFormModel.customMessage)
      }
      formData.append('sendInvitationNow', this.groupImportFormModel.sendInvitationNow)
      formData.append('idVerification', this.groupImportFormModel.idVerification)
      formData.append('hidePm', this.groupImportFormModel.hidePm)
      for (const key in params) {
        formData.append(key, params[key])
      }
      this.POST_MASS_IMPORT(formData)
        .then(() => {
          if (params?.confirmYearly) {
            toast.success(this.$t('project.members.dialogs.GroupImportDialog.messages.successAddedAndToSubMulti'))
          } else {
            toast.success(this.$t('project.members.dialogs.GroupImportDialog.messages.successAddedMulti'))
          }
          if (this.mnemo) {
            this.GET_GROUPS(this.mnemo)
          }
          track('mass_user_added', {
            project_mnemo: this.mnemo,
            project_type: this.isDataRoom ? PRODUCT_ROOM_TYPES.DATAROOM : PRODUCT_ROOM_TYPES.LEGAL_PROJECT,
            source: IMPORT_SOURCES.EXCEL,
          })
          this.closeDialog()
        })
        .catch(error => {
          if (error.response?.status === 300) {
            if (error.response?.data) {
              this.memberSubscriptionErrorData = error.response.data
              this.dialogContent = GROUP_IMPORT_DIALOG_CONTENT.MEMBER_SUBSCRIPTION
            }
          }
          if (error.response?.data) {
            const { data: errorData, errorCode, errorContext } = error.response.data
            this.errorCode = errorCode

            switch (errorCode) {
              case 'ERR_USER_ALREADY_IN_ROOM':
              case 'ERR_INVALID_INPUT': {
                toast.error(this.$t(`project.members.dialogs.GroupImportDialog.error.${errorCode}`))
                this.closeDialog()

                break
              }
              case 'ERR_FORBIDDEN_FOR_NON_SUB_USER': {
                this.dialogContent = GROUP_IMPORT_DIALOG_CONTENT.IRRECOVERABLE_ERROR
                this.subName = errorContext?.subName

                break
              }
              case 'ERR_FORBIDDEN_FOR_NON_SUB_ADMIN': {
                this.dialogContent = GROUP_IMPORT_DIALOG_CONTENT.IRRECOVERABLE_ERROR
                this.subName = errorContext?.subName
                this.subAdminContacts = errorContext?.subAdmins

                break
              }
              case 'ERR_FILE_NOT_ALLOWED':
              case 'ERR_FILE_NOT_PROCESSED': {
                this.massInputError = errorCode

                break
              }
              case 'ERR_INCORRECT_COLUMNS': {
                this.massInputError = errorCode
                this.$refs.groupImportForm.resetFileInput()

                break
              }
              case 'ERR_INCORRECT_DATA':
              case 'ERR_DUPLICATE_EMAIL':
              case 'ERR_DUPLICATE_USER': {
                this.massInputError = errorCode
                this.massInputErrorArray = errorData
                this.$refs.groupImportForm.resetFileInput()

                break
              }
            }
          } else {
            toast.error(this.$t('project.members.dialogs.GroupImportDialog.messages.errorGeneric'))
          }
        })
    },
  },
}
</script>
